import React, {useState, useEffect} from 'react';
import {Modal, Text, TextInput, SelectInput, Button, Radio} from '@/ui-kits';
import {SelectInputitem} from '@/interface';
import { chargeFilterLabelRegex } from '@/constants';

interface AddChargesModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {label: string; format: 'between' | 'greater_than'; from: string; to: string; status: boolean}) => void;
}

const statusOptions: SelectInputitem[] = [
  {id: 1, label: 'Active'},
  {id: 2, label: 'Inactive'},
];

export const AddChargesModal: React.FC<AddChargesModalProps> = ({open, onClose, onSave}) => {
  const [label, setLabel] = useState('');
  const [format, setFormat] = useState<'between' | 'greater_than'>('between');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState<SelectInputitem | null>(statusOptions[0]);

  // Helper function to validate and format decimal input
  const handleDecimalInput = (value: string): string => {
    // Allow empty string
    if (value === '') return '';

    // Remove any characters that aren't digits or decimal point
    let cleaned = value.replace(/[^\d.]/g, '');

    // Only allow one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }

    return cleaned;
  };

  useEffect(() => {
    if (open) {
      // Reset form when modal opens, ensuring "between" is selected
      setFormat('between');
      setLabel('');
      setFrom('');
      setTo('');
      setStatus(statusOptions[0]);
    }
  }, [open]);

  const isDirty = Boolean(
    label.trim() ||
      from.trim() ||
      to.trim() ||
      format !== 'between' ||
      (status?.id ?? statusOptions[0].id) !== statusOptions[0].id,
  );

  const closeModal = () => {
    if (!isDirty) onClose();
  };

  const handleSave = () => {
    if (!label || !from || !status) return;
    if (format === 'between' && !to) return;

    onSave({
      label,
      format,
      from,
      to: format === 'greater_than' ? 'infinity' : to,
      status: status.label === 'Active',
    });

    // Reset form
    setLabel('');
    setFormat('between');
    setFrom('');
    setTo('');
    setStatus(statusOptions[0]);
    onClose();
  };

  const isFormValid = label && from && status && (format === 'greater_than' || to);

  return (
    <Modal open={open} onClose={closeModal} className="relative bg-white py-6 px-8 rounded-xl gap-4 w-[90vw] max-w-[500px]">
      <Text variant="free" className="text-ui_blue font-FigtreeBold text-3xl xl mb-4">
        Add Charges
      </Text>

      <div className="flex flex-col gap-4">
        {/* Label Name */}
        <TextInput allowedRegex={chargeFilterLabelRegex} label="Label" value={label} onChangeText={value => setLabel(value)} placeholder="Enter Label name" className="w-full" required />

        {/* Choose Format */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <div className="flex items-center gap-2 sm:contents">
            <Text variant="body" className="font-FigtreeSemiBold text-base text-ui_blue whitespace-nowrap">
              Choose Format <span className="text-rejection">*</span>
            </Text>
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-4 mt-8 sm:mt-0">
              <Radio
                label="Between"
                value="between"
                onChange={checked => setFormat(checked ? 'between' : 'greater_than')}
                checked={format === 'between'}
                disabled={false}
              />
              <Radio
                label="Greater than"
                value="greater_than"
                onChange={checked => setFormat(checked ? 'greater_than' : 'between')}
                checked={format === 'greater_than'}
                disabled={false}
              />
            </div>
          </div>
        </div>

        {/* From */}
        <TextInput
          label="From"
          type="text"
          value={from}
          onChangeText={value => setFrom(handleDecimalInput(value))}
          placeholder="Enter Charges"
          className="w-full"
          required
        />

        {/* To - Only show when format is "between" */}
        {format === 'between' && (
          <TextInput
            label="To"
            type="text"
            value={to}
            onChangeText={value => setTo(handleDecimalInput(value))}
            placeholder="Enter Charges"
            className="w-full"
            required
          />
        )}

        {/* Status */}
        <SelectInput
          label="Status"
          value={status?.id || null}
          options={statusOptions}
          onChange={item => setStatus(item)}
          placeholder="Select Status"
          className="w-full"
          required
        />
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 mt-6">
        <Button variant="secondary" title="Cancel" onClick={onClose} className="flex-1" />
        <Button variant="primary" title="Save" onClick={handleSave} disabled={!isFormValid} className="flex-1" />
      </div>
    </Modal>
  );
};
